const regexp = new RegExp(String.fromCharCode(160), 'g');

type TFormatter = { format: (number: number) => string };

type FormatterCache = { [key: string]: Intl.NumberFormat | TFormatter };
type RegexpCache = { [key: string]: RegExp };

const _formatters: FormatterCache = {};
const _currencyFormatters: FormatterCache = {};
const _fractionRegExps: RegexpCache = {};

const getFractionRegExp = (suffix: string) => {
  const key = suffix || '_';
  if (!_fractionRegExps[key]) {
    _fractionRegExps[key] = new RegExp(`(,\\d{1})${suffix || ''}$`);
  }

  return _fractionRegExps[key];
};

const getFormatter = (culture: string, minimumFractionDigits = 0) => {
  const key = `${culture}_${minimumFractionDigits}`;

  if (!_formatters[key] && !Intl) {
    _formatters[key] = {
      format: (number: number) =>
        number.toLocaleString(culture, {
          style: 'decimal',
        }),
    };
  } else if (!_formatters[key]) {
    _formatters[key] = new Intl.NumberFormat(culture, {
      style: 'decimal',
      minimumFractionDigits,
    });
  }

  return _formatters[key];
};

const getCurrencyFormatter = (
  culture: string,
  currency: string,
  minimumFractionDigits?: number,
) => {
  const key = `${culture}_${currency}`;
  if (!_currencyFormatters[key] && !Intl) {
    _currencyFormatters[key] = {
      format: (amount: number) =>
        amount.toLocaleString(culture, {
          style: 'currency',
          currency,
        }),
    };
  } else if (!_currencyFormatters[key]) {
    _currencyFormatters[key] = new Intl.NumberFormat(culture, {
      style: 'currency',
      currency,
      minimumFractionDigits,
    });
  }

  return _currencyFormatters[key];
};

const currencyFormatter = (
  amount: number,
  currency: string | null,
  symbol: string,
  culture: string,
  minimumFractionDigits?: number,
) =>
  symbol || !currency
    ? `${getFormatter(culture, minimumFractionDigits).format(amount)}${symbol || ''}`
    : getCurrencyFormatter(culture, currency).format(amount);

export interface ICurrencyOptions {
  culture?: string;
  roundingFormatter?: (value: number) => number;
  suffix?: string;
  minimumFractionDigits?: number;
}

export const defaultOptions = {
  culture: 'de-DE',
  roundingFormatter: (value: number) => Math.round(value),
  suffix: ':-',
};

export const fi = {
  culture: 'fi-FI',
  suffix: ' €',
};

const formatPrice = (amount?: number, options?: ICurrencyOptions) => {
  const _options = { ...defaultOptions, ...(options || {}) };
  if ((!amount && amount !== 0) || isNaN(Number(amount))) {
    return '';
  }

  const currency = currencyFormatter(
    _options.roundingFormatter(amount),
    null,
    _options.suffix,
    _options.culture,
    _options.minimumFractionDigits,
  );
  const fractions = getFractionRegExp(_options.suffix);
  return currency.replace(regexp, ' ').replace(fractions, `$10${_options.suffix || ''}`);
};

export default formatPrice;

export const formatPriceExact = (amount?: number, options?: ICurrencyOptions) =>
  formatPrice(amount, {
    ...(options || {}),
    roundingFormatter: (value) => Math.round((value + Number.EPSILON) * 100) / 100,
  });

export const isDiscounted = (price: IPrice) => !!price.before && price.before !== price.price;

export const intToDecExact = (amount?: number, decimals = 2, options?: ICurrencyOptions) => {
  if ((!amount && amount !== 0) || isNaN(Number(amount))) {
    return '';
  }

  return formatPriceExact(amount / Math.pow(10, decimals), options);
};
